import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Menu, Dropdown, Button, Drawer, message } from "antd";
import { Country } from "country-state-city";
import {
  MenuOutlined,
  UserOutlined,
  DownOutlined,
  ArrowLeftOutlined,
  RightOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import ReactCountryFlag from "react-country-flag";
import styles from "./Header.module.scss";
import Logo from "../../assets/images/LogoV22.svg";
import LocationIcon from "../../assets/images/PlaneV2.png";
import { fetchAirports, fetchRoutesByCountry } from "../../store/countries";
import { AppState } from "../../store";
import { useTranslation } from "react-i18next";
import { addToSessionObject, BOOKING_SESSION_KEYS } from "../../utils";
import { DATE_FORMAT } from "../../utils/constants";
import moment from "moment";
import { ReactComponent as UserOutlinedSvg } from "../../assets/images/LoginV2.svg";
import { ReactComponent as RightIcon } from "../../assets/images/rightIcon.svg";
import { ReactComponent as LogoSvg } from "../../assets/images/LogoV22.svg";

import {
  Armchair,
  Luggage,
  Accessibility,
  CalendarRange,
  CalendarX2,
  LogIn,
  PawPrint,
  FileText,
  Baby,
  Users,
  ArrowLeftRight,
  ArrowRight,
} from "lucide-react";

import arrowdown from "../../assets/images/arrowdownHeaderV1.svg";
import arrowup from "../../assets/images/arrowUpHeaderV1.svg";
import calendarIcon from "../../assets/images/calendnarIcon.svg";

// import FlightActive from "../../assets/images/FlightInActive.png";
// import FlightInActive from "../../assets/images/FlightActive.png";
// import InfoActive from "../../assets/images/InfoActive.png";
// import InfoInActive from "../../assets/images/InfoInActive.png";

import LoginUserBlack from "../../assets/images/BlackLoginUser.svg";
// import InfoIconDropdown from "../../assets/images/InfoIcon.png";

import { logout } from "../../store/auth";

interface BookingBody {
  id: string;
  code: string;
  name: string;
}

const languageList = [
  { code: "en", countryCode: "GB", label: "English" },
  { code: "de", countryCode: "DE", label: "German" },
  { code: "fr", countryCode: "FR", label: "French" },
  { code: "sq", countryCode: "AL", label: "Shqip" },
];

const normalizeCountryName = (name: string) =>
  String(name || "").trim().toLowerCase();

const countryIsoByName = Country.getAllCountries().reduce<Record<string, string>>(
  (acc, country) => {
    acc[normalizeCountryName(country.name)] = country.isoCode;
    return acc;
  },
  {
    kosovo: "XK",
    macedonia: "MK",
  }
);

type SideDrawerMode = "routes" | "airports" | "info" | null;

const HeaderTop: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const airports = useSelector((state: AppState) => state.countries.countries);
  const routesByCountry = useSelector((state: AppState) => (state.countries as any).routesByCountry || []);
  const { accessToken, userFullName } = useSelector((state: AppState) => state.auth);
  const selectedDepartureFlight = useSelector((state: AppState) => state.selectedFlights.departureFlight);
  const selectedReturnFlight = useSelector((state: AppState) => state.selectedFlights.returnFlight);
  const oneWayFromRedux = useSelector((state: AppState) => state.home.oneWay);

  const isLoggedIn = !!accessToken;

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [languageModalVisible, setLanguageModalVisible] = useState(false);

  // desktop dropdown opens
  const [destinationsOpen, setDestinationsOpen] = useState(false);
  const [flightsOpen, setFlightsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [tempLanguage, setTempLanguage] = useState("en");

  const [sideDrawerVisible, setSideDrawerVisible] = useState(false);
  const [sideDrawerMode] = useState<SideDrawerMode>(null);

  const [selectedRoutesCountry] = useState<any | null>(null);
  const [selectedAirportsCountry] = useState<any | null>(null);
  const [selectedInfoMenu, setSelectedInfoMenu] = useState<any | null>(null);

  const [infoItemsVisible, setInfoItemsVisible] = useState(false);

  const [mobileAccountOpen, setMobileAccountOpen] = useState(false);
  const [isWide, setIsWide] = useState(window.innerWidth >= 1000);
  const [showFullPaxLabel, setShowFullPaxLabel] = useState(window.innerWidth >= 1240);

  // MOBILE accordion
  const [mobileSectionOpen, setMobileSectionOpen] = useState<"departure" | "flights" | "information" | null>(null);
  const [mobileFlightsCountry, setMobileFlightsCountry] = useState<any | null>(null);
  const [mobileDestinationsCountry, setMobileDestinationsCountry] = useState<any | null>(null);

  const isBookingRoute = location.pathname.startsWith("/booking");

  const stopAll = (e: any) => {
    if (!e) return;
    if (typeof e.preventDefault === "function") e.preventDefault();
    if (typeof e.stopPropagation === "function") e.stopPropagation();
  };

  const infoMenus = [
    {
      key: "menu1",
      icon: <Armchair size={24} color="#000" strokeWidth={1.5} />,
      titleKey: "info.menu.seats",
      bg: "white",
      items: [{ labelKey: "info.menu.seats", url: "/support?section=seats" }],
    },
    {
      key: "menu2",
      icon: <Luggage size={24} color="#000" strokeWidth={1.5} />,
      titleKey: "info.menu.baggage",
      bg: "white",
      items: [{ labelKey: "info.menu.baggage", url: "/support?section=baggage" }],
    },
    {
      key: "menu3",
      icon: <Accessibility size={24} color="#000" strokeWidth={1.5} />,
      titleKey: "info.menu.assist",
      bg: "white",
      items: [{ labelKey: "info.menu.assist", url: "/support?section=specialassistance" }],
    },
    {
      key: "menu4",
      icon: <CalendarRange size={24} color="#000" strokeWidth={1.5} />,
      titleKey: "info.menu.booking",
      bg: "white",
      items: [{ labelKey: "info.menu.booking", url: "/support?section=booking" }],
    },
    {
      key: "menu5",
      icon: <CalendarX2 size={24} color="#000" strokeWidth={1.5} />,
      titleKey: "info.menu.change",
      bg: "#FEF6F7",
      items: [{ labelKey: "info.menu.change", url: "/support?section=changeorcancel" }],
    },
    {
      key: "menu6",
      icon: <LogIn size={24} color="#000" strokeWidth={1.5} />,
      titleKey: "info.menu.checkin",
      bg: "#FEF6F7",
      items: [{ labelKey: "info.menu.checkin", url: "/support?section=checkinonline" }],
    },
    {
      key: "menu7",
      icon: <PawPrint size={24} color="#000" strokeWidth={1.5} />,
      titleKey: "info.menu.animals",
      bg: "#FEF6F7",
      items: [{ labelKey: "info.menu.animals", url: "/support?section=flyingwithanimals" }],
    },
    {
      key: "menu8",
      icon: <FileText size={24} color="#000" strokeWidth={1.5} />,
      titleKey: "info.menu.documents",
      bg: "#FEF6F7",
      items: [{ labelKey: "info.menu.documents", url: "/support?section=traveldocuments" }],
    },
    {
      key: "menu9",
      icon: <Baby size={24} color="#000" strokeWidth={1.5} />,
      titleKey: "info.menu.children",
      bg: "#FEF6F7",
      items: [{ labelKey: "info.menu.children", url: "/support?section=infants" }],
    },
  ];

  useEffect(() => {
    dispatch(fetchAirports() as any);
    dispatch(fetchRoutesByCountry() as any);

    const savedLang = sessionStorage.getItem("language") || "en";
    setSelectedLanguage(savedLang);
    setTempLanguage(savedLang);
  }, [dispatch]);

  useEffect(() => {
    if (isBookingRoute) {
      setDestinationsOpen(false);
      setFlightsOpen(false);
      setInfoOpen(false);
    }
  }, [isBookingRoute]);

  const handleLanguageSelect = (lang: string) => setTempLanguage(lang);

  const handleSaveLanguage = () => {
    setSelectedLanguage(tempLanguage);
    i18n.changeLanguage(tempLanguage);
    sessionStorage.setItem("language", tempLanguage);
    setLanguageModalVisible(false);
    setDrawerVisible(false);
  };

  const handleLogout = () => {
    dispatch(logout() as any);

    setDrawerVisible(false);
    setSideDrawerVisible(false);
    setInfoItemsVisible(false);
    setMobileAccountOpen(false);

    message.success({
      content: t("app.logout.success"),
      duration: 3,
    });

    history.push("/");
  };

  useEffect(() => {
    const handleResize = () => {
      setIsWide(window.innerWidth >= 1000);
      setShowFullPaxLabel(window.innerWidth >= 1240);
    };
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        if (destinationsOpen) setDestinationsOpen(false);
        if (flightsOpen) setFlightsOpen(false);
        if (infoOpen) setInfoOpen(false);
      }
    };

    window.addEventListener("resize", handleResize, { passive: true });
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [destinationsOpen, flightsOpen, infoOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setDrawerVisible(false);
        setSideDrawerVisible(false);
        setMobileAccountOpen(false);
        setMobileSectionOpen(null);
        setMobileFlightsCountry(null);
        setMobileDestinationsCountry(null);
        setSelectedInfoMenu(null);
        setInfoItemsVisible(false);
      }
    };

    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const cx = (...classes: (string | false | undefined)[]) => classes.filter(Boolean).join(" ");

  const selectedLangData = languageList.find((lang) => lang.code === selectedLanguage);

  const isAnyModalOpen = languageModalVisible || sideDrawerVisible || drawerVisible || infoItemsVisible;

  useEffect(() => {
    if (isAnyModalOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isAnyModalOpen]);

  const accountMenu = (
    <Menu>
      <Menu.Item
        key="account"
        onClick={() => {
          setMobileAccountOpen(false);
          history.push("/account-settings");
        }}
      >
        {t("app.header.myaccount") || "My bookings"}
      </Menu.Item>

      <Menu.Item
        key="logout"
        onClick={() => {
          setMobileAccountOpen(false);
          handleLogout();
        }}
      >
        {t("app.header.logout") || "Logout"}
      </Menu.Item>
    </Menu>
  );

  const airportNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    (airports || []).forEach((c: any) => {
      (c?.airports || []).forEach((a: any) => {
        const code = String(a?.code || "").toUpperCase();
        const name = String(a?.name || "");
        if (code && name) map.set(code, name);
      });
    });
    return map;
  }, [airports]);

  const getAirportLabel = (codeRaw: string) => {
    const code = String(codeRaw || "").toUpperCase();
    const name = airportNameByCode.get(code);
    if (name) return `${name} (${code})`;
    return code ? `(${code})` : "";
  };

  const getRouteLabel = (originCode: string, destinationCode: string) => {
    const originName = airportNameByCode.get(String(originCode || "").toUpperCase()) || "";
    const destName = airportNameByCode.get(String(destinationCode || "").toUpperCase()) || "";
    const originCodeUpper = String(originCode || "").toUpperCase();
    const destCodeUpper = String(destinationCode || "").toUpperCase();
    if (originName && destName) {
      return `${originName} (${originCodeUpper}) - ${destName} (${destCodeUpper})`;
    }
    return `${originCodeUpper} - ${destCodeUpper}`;
  };

  const getCountryRouteLabel = (departureCountry: string, arrivalCountry: string) => {
    return `${departureCountry} to ${arrivalCountry}`;
  };

  const countryIso2 = (name: string) => {
    return countryIsoByName[normalizeCountryName(name)] || "GB";
  };

  const destinationsConsolidated = useMemo(() => {
    const map = new Map<string, any>();
    (routesByCountry || []).forEach((item: any) => {
      const countryName = String(item?.countryName || "");
      if (!map.has(countryName)) {
        map.set(countryName, {
          countryName,
          routes: [],
        });
      }
      const existing = map.get(countryName)!;
      existing.routes.push(...(item?.flightRoute || []));
    });
    return Array.from(map.values());
  }, [routesByCountry]);

  const destinationsMenu = (
    <div className={styles["destinations-dropdown-content-v2"]}>
      <div className={`${isWide ? "container" : ""}`}>
        <div className={styles["destinations-header"]}>
          <span>{t("app.header.destinations") || "Destinations"}</span>
        </div>

        <div className={styles["destinations-grid"]}>
          {destinationsConsolidated.map((c: any, idx: number) => (
            <div key={`${c?.countryName || "country"}-${idx}`} className={styles["destinations-country-block"]}>
              <div className={styles["destinations-country-title"]}>
                <ReactCountryFlag
                  countryCode={countryIso2(c?.countryName)}
                  svg
                  style={{ width: "22px", height: "16px" }}
                />
                <span>{String(c?.countryName || "").toUpperCase()}</span>
              </div>

              <div className={styles["destinations-routes"]}>
                {(c?.routes || []).map((r: any, rIdx: number) => {
                  const routeLabel = getRouteLabel(r?.origin, r?.destination);
                  return (
                    <div
                      key={`${r?.origin || ""}-${r?.destination || ""}-${rIdx}`}
                      className={styles["destinations-route-item"]}
                      onClick={() => {
                        history.push(`/?from=${r.origin}&to=${r.destination}`);
                        setDestinationsOpen(false);
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      {routeLabel}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const flightsRoutesFiltered = useMemo(() => {
    return routesByCountry || [];
  }, [routesByCountry]);

  const flightsByCountry = useMemo(() => {
    const map = new Map<string, any>();
    const routeKeysByCountry = new Map<string, Set<string>>();

    flightsRoutesFiltered.forEach((item: any) => {
      const countryName = String(item?.countryName || item?.departureCountry || "");
      const departureCountry = String(item?.departureCountry || countryName);
      const arrivalCountry = String(item?.arrivalCountry || "");

      if (!map.has(countryName)) {
        map.set(countryName, {
          countryName,
          routes: [],
        });
        routeKeysByCountry.set(countryName, new Set<string>());
      }

      const existing = map.get(countryName)!;
      const existingRouteKeys = routeKeysByCountry.get(countryName)!;

      const firstRoute = (item?.flightRoute || [])[0] || {};
      const routeKey = `${departureCountry.toLowerCase()}-${arrivalCountry.toLowerCase()}`;

      if (existingRouteKeys.has(routeKey)) return;
      existingRouteKeys.add(routeKey);

      existing.routes.push({
        departureCountry,
        arrivalCountry,
        origin: String(firstRoute?.origin || ""),
        destination: String(firstRoute?.destination || ""),
      });
    });
    return Array.from(map.values());
  }, [flightsRoutesFiltered]);

  const flightsMenu = (
    <div className={styles["flights-dropdown-content-v2"]}>
      <div className={`${isWide ? "container" : ""}`}>
        <div className={styles["flights-header"]}>
          <span>{t("app.header.flights") || "Flights"}</span>
        </div>
        <div className={styles["flights-grid"]}>
          {flightsByCountry.map((countryGroup: any, idx: number) => (
            <div key={`flights-country-${idx}`} className={styles["flights-country-block"]}>
              <div className={styles["flights-country-title"]}>
                <ReactCountryFlag
                  countryCode={countryIso2(countryGroup.countryName)}
                  svg
                  style={{ width: "22px", height: "16px" }}
                />
                <span>{String(countryGroup.countryName || "").toUpperCase()}</span>
              </div>
              <div className={styles["flights-routes"]}>
                {countryGroup.routes.map((route: any, rIdx: number) => {
                  const routeLabel = getCountryRouteLabel(route?.departureCountry, route?.arrivalCountry);
                  return (
                    <div
                      key={`route-${route?.origin}-${route?.destination}-${rIdx}`}
                      className={styles["flights-route-item"]}
                      onClick={() => {
                        history.push(`/?from=${route.origin}&to=${route.destination}`);
                        setFlightsOpen(false);
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      {routeLabel}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const infoMenuDesktop = (
    <div className={styles["info-dropdown-wrapper"]}>
      <div className={`${isWide ? "container" : ""}`}>
        <div className={styles["info-header-top"]}>
          <span>{t("info.header.title")}</span>
        </div>

        <div className={styles["info-row"]}>
          {infoMenus.slice(0, 4).map((menu) => (
            <div key={menu.key} className={styles["info-block"]}>
              <div className={styles["info-header"]}>
                {menu.icon}
                <span>{t(menu.titleKey)}</span>
              </div>
              <ul>
                {menu.items.map((item, idx) => (
                  <li key={idx} onClick={() => history.push(item.url)}>
                    {t(item.labelKey)} <RightIcon />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className={`${styles["info-row"]} ${styles["pink-bg"]}`}>
          {infoMenus.slice(4).map((menu) => (
            <div key={menu.key} className={styles["info-block"]}>
              <div className={styles["info-header"]}>
                {menu.icon}
                <span>{t(menu.titleKey)}</span>
              </div>
              <ul>
                {menu.items.map((item, idx) => (
                  <li key={idx} onClick={() => history.push(item.url)}>
                    {t(item.labelKey)} <RightIcon />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const [bookingDataRefresh, setBookingDataRefresh] = useState(0);

  useEffect(() => {
    setBookingDataRefresh((prev) => prev + 1);
  }, [location.pathname]);

  const bookingData = useMemo(() => {
    let homeData: any = null;
    let flightsData: any = null;

    try {
      const homeDataStr = (window as any).sessionStorage.getItem("pr_jet_home_data");
      if (homeDataStr) {
        homeData = JSON.parse(homeDataStr);
        // Convert date strings back to moment objects if needed
        if (homeData.dates && Array.isArray(homeData.dates)) {
          homeData.dates = homeData.dates.map((d: any) => {
            if (!d) return null;
            if (moment.isMoment(d)) return d;
            return moment(d);
          });
        }
      }
    } catch (e) {
      console.warn("Failed to read home data from sessionStorage", e);
    }

    try {
      const flightsDataStr = (window as any).sessionStorage.getItem(BOOKING_SESSION_KEYS.FLIGHTS);
      if (flightsDataStr) {
        flightsData = JSON.parse(flightsDataStr);
      }
    } catch (e) {
      console.warn("Failed to read flights data from sessionStorage", e);
    }

    return { homeData, flightsData };
  }, [bookingDataRefresh, location.pathname]); // Re-read when route changes or refresh triggered

  useEffect(() => {
    const home = bookingData.homeData;
    const a = home?.adults ?? 0;
    const c = home?.children ?? 0;
    const i = home?.infants ?? 0;
    const sum = (typeof a === "number" ? a : parseInt(String(a), 10) || 0) +
      (typeof c === "number" ? c : parseInt(String(c), 10) || 0) +
      (typeof i === "number" ? i : parseInt(String(i), 10) || 0);
    if (sum >= 1) return;
    try {
      if (!home) {
        (window as any).sessionStorage.setItem(
          "pr_jet_home_data",
          JSON.stringify({ adults: 1, children: 0, infants: 0 })
        );
      } else {
        addToSessionObject("pr_jet_home_data", "adults", 1);
        addToSessionObject("pr_jet_home_data", "children", 0);
        addToSessionObject("pr_jet_home_data", "infants", 0);
      }
      setBookingDataRefresh((prev) => prev + 1);
    } catch (e) {
      console.warn("Failed to write default passengers to session", e);
    }
  }, [bookingData.homeData]);

  const formatShortDate = (date: moment.Moment | string | null, locale?: string) => {
    if (!date) return "";
    let dt: moment.Moment;
    if (moment.isMoment(date)) {
      dt = date;
    } else if (typeof date === "string") {
      dt = moment(date, DATE_FORMAT, true).isValid()
        ? moment(date, DATE_FORMAT)
        : moment(date);
    } else {
      dt = moment(date);
    }
    if (!dt.isValid()) return "";
    const normalizedLocale = locale ? locale.toLowerCase().replace("_", "-") : "";
    const m =
      normalizedLocale && moment.locales().includes(normalizedLocale)
        ? dt.clone().locale(normalizedLocale)
        : dt;
    const day = String(m.date()).padStart(2, "0");
    const month = m.format("MMM").toUpperCase();
    return `${day} ${month}`;
  };

  const getAirportName = (code: string) => {
    if (!code) return "—";
    const airport = airports
      .flatMap((c: any) => c.airports || [])
      .find((a: any) => a.code === code);
    return airport?.name?.toUpperCase() || code.toUpperCase();
  };

  const fromAirport = bookingData.homeData?.fromAirport || "";
  const toAirport = bookingData.homeData?.toAirport || "";
  const dates = bookingData.homeData?.dates || [];
  const adults = bookingData.homeData?.adults || 0;
  const children = bookingData.homeData?.children || 0;
  const infants = bookingData.homeData?.infants || 0;
  const returnFlight = bookingData.flightsData?.returnFlight;
  const oneWay =
    typeof oneWayFromRedux === "boolean"
      ? oneWayFromRedux
      : Boolean(bookingData.homeData?.oneWay);

  const fromLabel = getAirportName(fromAirport);
  const toLabel = getAirportName(toAirport);
  const hasReturnFlight = Boolean(returnFlight || selectedReturnFlight);
  const isReturnTrip = !oneWay && hasReturnFlight;
  const isOneWayTicket = oneWay || !hasReturnFlight;

  // Prefer dates from selected flights (Redux) so header updates when user picks a date on Flights page
  const departureDateDisplay = selectedDepartureFlight?.dateOfDeparture || bookingData.flightsData?.departureFlight?.dateOfDeparture || dates[0];
  const returnDateDisplay = selectedReturnFlight?.dateOfDeparture || bookingData.flightsData?.returnFlight?.dateOfDeparture || dates[1];

  const currentLocale = i18n.language || "en";
  const bookingDatesLabel = (() => {
    const d1 = formatShortDate(departureDateDisplay, currentLocale);
    const d2 = formatShortDate(returnDateDisplay, currentLocale);
    if (isReturnTrip && d1 && d2) return `${d1} - ${d2}`;
    if (d1) return d1;
    return "—";
  })();

  const totalPassengersRaw =
    (typeof adults === "number" ? adults : parseInt(String(adults), 10) || 0) +
    (typeof children === "number" ? children : parseInt(String(children), 10) || 0) +
    (typeof infants === "number" ? infants : parseInt(String(infants), 10) || 0);
  const totalPassengers = totalPassengersRaw >= 1 ? totalPassengersRaw : 1;
  const bookingPaxLabel =
    totalPassengers === 1
      ? `1 ${t("app.home.form.passengers.passengerSingular", "passenger")}`
      : `${totalPassengers} ${t("app.home.form.passengers.passengerPlural", "passengers")}`;
  const bookingPaxLabelMobile = `${totalPassengers} ${t("app.bookFlight.passengers.passShort", "PASS")}`;

  const BookingSummary = ({ variant }: { variant: "desktop" | "mobile" }) => {
    const RouteIcon = isOneWayTicket ? ArrowRight : ArrowLeftRight;

    if (variant === "desktop") {
      return (
        <div className={styles["booking-summary-desktop-v2"]}>
          <div className={styles["booking-pill-v2"]}>
            <div className={styles["booking-desktop-items-v2"]}>
              <div className={styles["booking-desktop-item-v2"]}>
                <span className={styles["booking-desktop-text-v2"]}>{fromLabel}</span>
                <div className={styles["booking-route-icon-wrap-v2"]} aria-hidden="true">
                  <RouteIcon className={styles["booking-route-icon-v2"]} />
                </div>
                <span className={styles["booking-desktop-text-v2"]}>{toLabel}</span>
              </div>

              <div className={`${styles["booking-desktop-item-v2"]} ${styles["booking-desktop-date-v2"]}`}>
                <img src={calendarIcon} alt="" className={styles["booking-desktop-icon-24-v2"]} aria-hidden />
                <span className={styles["booking-desktop-text-v2"]}>{bookingDatesLabel}</span>
              </div>

              <div className={`${styles["booking-desktop-item-v2"]} ${styles["booking-desktop-pax-v2"]}`}>
                <Users className={styles["booking-desktop-icon-24-v2"]} />
                <span className={styles["booking-desktop-text-v2"]}>
                  {showFullPaxLabel ? bookingPaxLabel : bookingPaxLabelMobile}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles["booking-summary-mobile-v2"]}>
        <div className={styles["booking-pill-v2"]}>
          <div className={styles["booking-iconbox-v2"]} aria-hidden="true">
            <img src={calendarIcon} alt="" className={styles["booking-iconbox-icon-v2"]} aria-hidden />
          </div>

          <div className={styles["booking-mobile-center-v2"]}>
            <div className={styles["booking-route-v2"]}>
              <span className={styles["booking-route-city-v2"]}>{fromLabel}</span>
              <div className={styles["booking-route-icon-wrap-v2"]} aria-hidden="true">
                <RouteIcon className={styles["booking-route-icon-v2"]} />
              </div>
              <span className={styles["booking-route-city-v2"]}>{toLabel}</span>
            </div>

            <div className={styles["booking-meta-v2"]}>
              <span>{bookingDatesLabel}</span>
              <span className={styles["booking-sep-v2"]}>|</span>
              <span>{bookingPaxLabelMobile}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const toggleMobileSection = (key: "departure" | "flights" | "information") => {
    setMobileSectionOpen((prev) => (prev === key ? null : key));
  };

  const handleInfoItemClick = (url: string) => {
    history.push(url);
    setDrawerVisible(false);
    setSideDrawerVisible(false);
    setInfoItemsVisible(false);
  };

  return (
    <>
      <div className={`${styles["header-v2"]} ${styles["header-top-v2"]}`}>
        <div className={`${styles["promo-bar-v2"]}`}>
          <div className={cx(isWide && "container", styles.headerPromoV2)}>
            <span className={styles["promo-text-v2"]}>{t("app.header.promo")}</span>

            <div className={styles["promo-links-v2"]}>
              <Link to="/contact">{t("app.header.contact")}</Link>

              <a href="#">
                <QuestionCircleOutlined style={{ marginRight: 6 }} />
                {t("app.header.helpcenter")}
              </a>

              {!isLoggedIn ? (
                <Button
                  icon={
                    <span style={{ display: "inline-block", position: "relative", top: "-1.6px", right: "5px" }}>
                      <UserOutlinedSvg />
                    </span>
                  }
                  className={`${styles["login-btn-v2"]} header-login-btn`}
                  type="default"
                  onClick={() => history.push("/login")}
                >
                  {t("app.header.loginregistration")}
                </Button>
              ) : (
                <Dropdown overlay={accountMenu} trigger={["click"]} placement="bottomRight">
                  <Button
                    className={`${styles["login-btn-v2"]} header-login-btn`}
                    type="default"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <UserOutlined style={{ marginRight: 8 }} />
                    {userFullName || t("app.header.myaccount") || "My account"}
                    <DownOutlined style={{ marginLeft: 6, fontSize: 10 }} />
                  </Button>
                </Dropdown>
              )}
            </div>
          </div>
        </div>

        <div className={styles["nav-bar-v2"]}>
          <div className={`${isWide ? "container" : ""} d-flex justify-content-between align-items-center`}>
            <Link to="/" className={styles["logo-link-v2"]}>
              <LogoSvg />
            </Link>

            {isBookingRoute && <BookingSummary variant="desktop" />}

            <div className={styles["menu-desktop-v2"]}>
              {!isBookingRoute ? (
                <>
                  {/* DESTINATIONS */}
                  <Dropdown
                    overlay={destinationsMenu}
                    trigger={["click"]}
                    open={destinationsOpen}
                    onOpenChange={(open) => setDestinationsOpen(open)}
                    overlayClassName={`${styles["header-destinations-dropdown-v2"]} header-flights-dropdown`}
                    overlayStyle={{ width: "100%", zIndex: 999999, top: "95px" }}
                  >
                    <a className={cx(styles["menu-link-v2"], destinationsOpen && styles["menu-link-v2-open"])}>
                      {t("app.header.destinations") || "Destinations"}{" "}
                      <img
                        src={destinationsOpen ? arrowup : arrowdown}
                        alt="arrow"
                        className={styles["menu-arrow"]}
                        width={12}
                        height={12}
                        decoding="async"
                      />
                    </a>
                  </Dropdown>

                  {/* FLIGHTS */}
                  <Dropdown
                    overlay={flightsMenu}
                    trigger={["click"]}
                    open={flightsOpen}
                    onOpenChange={(open) => setFlightsOpen(open)}
                    overlayClassName={`${styles["header-flights-dropdown-v2"]} header-flights-dropdown`}
                    overlayStyle={{ width: "100%", zIndex: 999999, top: "95px" }}
                  >
                    <a className={cx(styles["menu-link-v2"], flightsOpen && styles["menu-link-v2-open"])}>
                      {t("app.header.flights") || "Flights"}{" "}
                      <img src={flightsOpen ? arrowup : arrowdown} alt="arrow" className={styles["menu-arrow"]} width={12} height={12} decoding="async" />
                    </a>
                  </Dropdown>

                  {/* INFORMATION */}
                  <Dropdown
                    overlay={infoMenuDesktop}
                    trigger={["click"]}
                    open={infoOpen}
                    onOpenChange={(open) => setInfoOpen(open)}
                    overlayClassName={`${styles["header-flights-dropdown-v2"]} header-flights-dropdown`}
                    overlayStyle={{ width: "100%", zIndex: 999999, top: "95px" }}
                  >
                    <a className={cx(styles["menu-link-v2"], infoOpen && styles["menu-link-v2-open"])}>
                      {t("app.header.information") || "Information"}{" "}
                      <img src={infoOpen ? arrowup : arrowdown} alt="arrow" className={styles["menu-arrow"]} width={12} height={12} decoding="async" />
                    </a>
                  </Dropdown>
                </>
              ) : null}

              <Button
                icon={
                  <ReactCountryFlag
                    countryCode={selectedLangData?.countryCode || "GB"}
                    svg
                    style={{ width: "24px", height: "18px", marginRight: 5 }}
                  />
                }
                onClick={() => setLanguageModalVisible(true)}
                className="header-language-btn"
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  backgroundColor: "white",
                  padding: "8px 12px",
                  maxHeight: "40px",
                  fontSize: "16px",
                  fontWeight: "400",
                  color: "#000929",
                }}
              >
                {selectedLangData?.label}
              </Button>
            </div>
          </div>

          <div className={styles["mobile-right-icons"]}>
            <ReactCountryFlag
              countryCode={selectedLangData?.countryCode || "GB"}
              svg
              style={{ fontSize: "1em", cursor: "pointer" }}
              onClick={() => setLanguageModalVisible(true)}
            />

            <QuestionCircleOutlined className={styles.customIcon} onClick={() => { }} />

            {!isLoggedIn ? (
              <img
                src={LoginUserBlack}
                alt="Login"
                style={{ width: "24px", height: "24px", cursor: "pointer" }}
                width={24}
                height={24}
                decoding="async"
                onClick={(e) => {
                  stopAll(e);
                  history.push("/login");
                }}
              />
            ) : (
              <Dropdown
                overlay={accountMenu}
                trigger={[]}
                placement="bottomRight"
                open={mobileAccountOpen}
                overlayClassName={styles["mobile-account-dropdown"]}
                getPopupContainer={() => document.body}
                overlayStyle={{ zIndex: 2000000 }}
              >
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMobileAccountOpen((v) => !v);
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      setMobileAccountOpen((v) => !v);
                    }
                  }}
                  style={{ display: "inline-flex" }}
                >
                  <img src={LoginUserBlack} alt="Account" style={{ width: "24px", height: "24px", cursor: "pointer" }} width={24} height={24} decoding="async" />
                </span>
              </Dropdown>
            )}

            <MenuOutlined
              className={styles.customIcon}
              onClick={(e) => {
                stopAll(e);
                setDrawerVisible(true);
              }}
            />
          </div>

          {isBookingRoute && <BookingSummary variant="mobile" />}
        </div>
      </div>

      {/* MOBILE MAIN DRAWER (Accordion) */}
      <Drawer
        title={null}
        placement="left"
        closable={false}
        onClose={() => {
          setDrawerVisible(false);
          setSideDrawerVisible(false);
          setInfoItemsVisible(false);
          setMobileSectionOpen(null);
          setMobileFlightsCountry(null);
          setMobileDestinationsCountry(null);
          setSelectedInfoMenu(null);
        }}
        open={drawerVisible}
        bodyStyle={{ background: "#FFFFFF", padding: 0 }}
        className={styles["custom-header-drawer"]}
        width="100%"
      >
        <div className={styles["drawer-header"]}>
          <img src={Logo} alt="Logo" className={styles["drawer-logo"]} width={140} height={32} decoding="async" />
          <button
            className={styles["drawer-close-btn"]}
            onClick={() => {
              setDrawerVisible(false);
              setSideDrawerVisible(false);
              setInfoItemsVisible(false);
              setMobileSectionOpen(null);
            }}
          >
            ✕
          </button>
        </div>

        <div className={styles["drawer-accordion"]}>
          {/* DEPARTURE (Destinations) */}
          <button
            className={cx(styles["drawer-accordion-head"], mobileSectionOpen === "departure" && styles["open"])}
            onClick={() => toggleMobileSection("departure")}
          >
            <span className={styles["drawer-accordion-title"]}>{t("app.header.destinations") || "Departure"}</span>
            <span className={styles["drawer-accordion-icon"]}>
              <img src={mobileSectionOpen === "departure" ? arrowup : arrowdown} alt="toggle" width={12} height={12} decoding="async" />
            </span>
          </button>

          {mobileSectionOpen === "departure" && (
            <div className={styles["drawer-accordion-body"]}>
              {destinationsConsolidated.map((country: any, idx: number) => {
                const isSelected = mobileDestinationsCountry?.countryName === country?.countryName;
                return (
                  <div key={`dest-country-${idx}`}>
                    <div
                      className={cx(
                        styles["drawer-country-row"],
                        isSelected && styles["drawer-country-row-selected"]
                      )}
                      onClick={() =>
                        setMobileDestinationsCountry(isSelected ? null : country)
                      }
                    >
                      <ReactCountryFlag
                        countryCode={countryIso2(country?.countryName)}
                        svg
                        style={{ width: "24px", height: "18px" }}
                      />
                      <span className={styles["drawer-country-name"]}>
                        {String(country?.countryName || "").toUpperCase()}
                      </span>
                      <img
                        src={isSelected ? arrowup : arrowdown}
                        alt="toggle"
                        className={cx(
                          styles["drawer-row-arrow"],
                          isSelected && styles["drawer-row-arrow-selected"]
                        )}
                      />
                    </div>
                    {isSelected && (
                      <div className={styles["drawer-routes-list"]}>
                        {(country?.routes || []).map((r: any, rIdx: number) => {
                          const routeLabel = getRouteLabel(r?.origin, r?.destination);
                          return (
                            <div
                              key={`dest-route-${rIdx}`}
                              className={styles["drawer-route-item"]}
                              onClick={() => {
                                history.push(`/?from=${r.origin}&to=${r.destination}`);
                                setDrawerVisible(false);
                                setMobileDestinationsCountry(null);
                              }}
                            >
                              {routeLabel}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* FLIGHTS */}
          <button
            className={cx(styles["drawer-accordion-head"], mobileSectionOpen === "flights" && styles["open"])}
            onClick={() => toggleMobileSection("flights")}
          >
            <span className={styles["drawer-accordion-title"]}>{t("app.header.flights") || "Flights"}</span>
            <span className={styles["drawer-accordion-icon"]}>
              <img src={mobileSectionOpen === "flights" ? arrowup : arrowdown} alt="toggle" width={12} height={12} decoding="async" />
            </span>
          </button>

          {mobileSectionOpen === "flights" && (
            <div className={styles["drawer-accordion-body"]}>
              {flightsByCountry.map((countryGroup: any, idx: number) => {
                const isSelected = mobileFlightsCountry?.countryName === countryGroup?.countryName;
                return (
                  <div key={`flights-country-${idx}`}>
                    <div
                      className={cx(
                        styles["drawer-country-row"],
                        isSelected && styles["drawer-country-row-selected"]
                      )}
                      onClick={() =>
                        setMobileFlightsCountry(isSelected ? null : countryGroup)
                      }
                    >
                      <ReactCountryFlag
                        countryCode={countryIso2(countryGroup.countryName)}
                        svg
                        style={{ width: "24px", height: "18px" }}
                      />
                      <span className={styles["drawer-country-name"]}>
                        {String(countryGroup?.countryName || "").toUpperCase()}
                      </span>
                      <img
                        src={isSelected ? arrowup : arrowdown}
                        alt="toggle"
                        className={cx(
                          styles["drawer-row-arrow"],
                          isSelected && styles["drawer-row-arrow-selected"]
                        )}
                      />
                    </div>
                    {isSelected && (
                      <div className={styles["drawer-routes-list"]}>
                        {countryGroup.routes.map((route: any, rIdx: number) => {
                          const routeLabel = getCountryRouteLabel(route?.departureCountry, route?.arrivalCountry);
                          return (
                            <div
                              key={`flights-route-${rIdx}`}
                              className={styles["drawer-route-item"]}
                              onClick={() => {
                                if (route?.origin && route?.destination) {
                                  history.push(`/?from=${route.origin}&to=${route.destination}`);
                                }
                                setDrawerVisible(false);
                                setMobileFlightsCountry(null);
                              }}
                            >
                              {routeLabel}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* INFORMATION */}
          <button
            className={cx(styles["drawer-accordion-head"], mobileSectionOpen === "information" && styles["open"])}
            onClick={() => toggleMobileSection("information")}
          >
            <span className={styles["drawer-accordion-title"]}>{t("app.header.information") || "Information"}</span>
            <span className={styles["drawer-accordion-icon"]}>
              <img src={mobileSectionOpen === "information" ? arrowup : arrowdown} alt="toggle" width={12} height={12} decoding="async" />
            </span>
          </button>

          {mobileSectionOpen === "information" && (
            <div className={styles["drawer-accordion-body"]}>
              {infoMenus.map((menu) => {
                const isSelected = selectedInfoMenu?.key === menu.key;
                return (
                  <div key={menu.key}>
                    <div
                      className={cx(
                        styles["drawer-info-row"],
                        isSelected && styles["drawer-info-row-selected"]
                      )}
                      onClick={() => setSelectedInfoMenu(isSelected ? null : menu)}
                    >
                      <div className={styles["drawer-info-icon"]}>{menu.icon}</div>
                      <span className={styles["drawer-info-title"]}>{t(menu.titleKey)}</span>
                      <img
                        src={isSelected ? arrowup : arrowdown}
                        alt="toggle"
                        className={cx(
                          styles["drawer-row-arrow"],
                          isSelected && styles["drawer-row-arrow-selected"]
                        )}
                      />
                    </div>
                    {isSelected && (
                      <div className={styles["drawer-routes-list"]}>
                        {menu.items.map((item: any, idx: number) => (
                          <div
                            key={idx}
                            className={styles["drawer-route-item"]}
                            onClick={() => handleInfoItemClick(item.url)}
                          >
                            {t(item.labelKey)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles["drawer-bottom-items"]}>
          <button
            className={styles["drawer-link-btn"]}
            onClick={() => {
              setDrawerVisible(false);
              history.push("/contact");
            }}
          >
            {t("app.header.contact")}
          </button>
          <button className={styles["drawer-link-btn"]}>{t("app.header.helpcenter")}</button>

          {!isLoggedIn ? (
            <button
              className={styles["drawer-login-btn"]}
              onClick={() => {
                setDrawerVisible(false);
                history.push("/login");
              }}
            >
              <UserOutlined style={{ marginRight: "8px" }} />
              {t("app.header.loginregistration")}
            </button>
          ) : null}
        </div>
      </Drawer>

      {/* SIDE DRAWER */}
      <Drawer
        title={null}
        placement="right"
        closable={false}
        onClose={() => setSideDrawerVisible(false)}
        open={sideDrawerVisible}
        bodyStyle={{ background: "#FFFFFF", padding: 0 }}
        className={styles["cities-side-drawer"]}
        width="100%"
        mask={false}
      >
        {/* ROUTES country */}
        {sideDrawerMode === "routes" && selectedRoutesCountry && (
          <>
            <div className={styles["side-drawer-header"]}>
              <ArrowLeftOutlined onClick={() => setSideDrawerVisible(false)} className={styles["back-icon"]} />
              <span>{String(selectedRoutesCountry?.countryName || "").toUpperCase()}</span>
            </div>

            <div className={styles["destinations-mobile-list"]}>
              {(selectedRoutesCountry?.flightRoute || []).map((r: any, idx: number) => {
                const from = getAirportLabel(r?.origin);
                const to = getAirportLabel(r?.destination);
                return (
                  <div
                    key={`${r?.origin || ""}-${r?.destination || ""}-${idx}`}
                    className={styles["destinations-mobile-route"]}
                    onClick={() => console.log("route", r)}
                    role="button"
                    tabIndex={0}
                  >
                    {from} - {to}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* AIRPORTS country */}
        {sideDrawerMode === "airports" && selectedAirportsCountry && (
          <>
            <div className={styles["side-drawer-header"]}>
              <ArrowLeftOutlined onClick={() => setSideDrawerVisible(false)} className={styles["back-icon"]} />
              <span>{String(selectedAirportsCountry?.name || "").toUpperCase()}</span>
            </div>

            <div className={styles["cities-content"]}>
              {(selectedAirportsCountry?.airports || []).map((airport: BookingBody) => (
                <div key={airport.id} className={styles["city-item"]} onClick={() => console.log(airport.code)}>
                  <img src={LocationIcon} alt="Location" className={styles["location-icon"]} />
                  <div className={styles["city-info"]}>
                    <span className={styles["city-name"]}>{airport.name}</span>
                    <span className={styles["city-code"]}>({airport.code})</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* INFO */}
        {sideDrawerMode === "info" && selectedInfoMenu && (
          <>
            <div className={styles["side-drawer-header"]}>
              <ArrowLeftOutlined
                onClick={() => {
                  setSideDrawerVisible(false);
                  setInfoItemsVisible(false);
                }}
                className={styles["back-icon"]}
              />
              <span>{t(selectedInfoMenu.titleKey)}</span>
            </div>

            {infoItemsVisible ? (
              <div className={styles["cities-content"]}>
                {selectedInfoMenu.items.map((item: any, idx: number) => (
                  <div key={idx} className={styles["city-item"]} onClick={() => handleInfoItemClick(item.url)}>
                    <div className={styles["city-info"]}>
                      <span className={styles["city-name"]}>{t(item.labelKey)}</span>
                    </div>
                    <RightOutlined className={styles["arrow-icon"]} />
                  </div>
                ))}
              </div>
            ) : null}
          </>
        )}
      </Drawer>

      {/* LANGUAGE MODAL */}
      {languageModalVisible && (
        <div className={styles["custom-language-modal"]}>
          <div className={styles["modal-content"]}>
            <div className={styles["modal-header"]}>
              <h3 className={styles["modal-title"]}>{t("app.header.chooselanguage")}</h3>
              <button className={styles["modal-close-btn"]} onClick={() => setLanguageModalVisible(false)}>
                ✕
              </button>
            </div>

            <div className={styles["language-grid"]}>
              {languageList.map((lang) => (
                <button
                  key={lang.code}
                  className={`${styles["language-btn"]} ${tempLanguage === lang.code ? styles.selected : ""}`}
                  onClick={() => handleLanguageSelect(lang.code)}
                >
                  <ReactCountryFlag countryCode={lang.countryCode} svg style={{ fontSize: "1.5em", marginRight: "8px" }} />
                  {lang.label}
                </button>
              ))}
            </div>

            <div className={styles["modal-footer"]}>
              <button className={styles["cancel-btn"]} onClick={() => setLanguageModalVisible(false)}>
                {t("app.header.cancel")}
              </button>
              <button className={styles["save-btn"]} onClick={handleSaveLanguage}>
                {t("app.header.done")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HeaderTop;
